import {
  cardanoAddressSchema,
  cardanoStakeAddressSchema,
  cardanoTxHashSchema,
  createCardanoRewardSourceId,
  createCardanoTxSourceId,
} from '@pkg/cardano'
import {
  createXrplSourceId,
  xrplClassicAddressSchema,
  xrplHashSchema,
} from '@pkg/xrpl'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import {
  accountEvmCsvTable,
  accountExchangeApiTable,
  accountSolanaSignatureTable,
  accountTable,
  accountingPeriodTable,
  actionSourceTable,
  assistantProposalActionTable,
  assistantProposalTable,
  assistantRejectedFingerprintTable,
  commentCandidateTable,
  historyFilterPresetTable,
  singleActionRuleTable,
} from './schema'

/** Mainnet の Classic Address を同期キーとする XRPL アカウント。 */
export const accountXrplSchema = createSelectSchema(accountTable)
  .pick({
    id: true,
    group: true,
    name: true,
    type: true,
    category: true,
    syncing: true,
    lastSyncedAt: true,
    xrplAddress: true,
    xrplIndexComplete: true,
  })
  .extend({
    type: z.literal('xrpl'),
    category: z.literal('xrpl'),
    xrplAddress: xrplClassicAddressSchema,
    syncing: z
      .boolean()
      .nullable()
      .transform((value) => value ?? false),
    xrplIndexComplete: z
      .boolean()
      .nullable()
      .transform((value) => value ?? false),
  })

/** XRPL Source の hash と決定的 ID の一致を検証する。 */
export const actionSourceXrplSchema = z
  .object({
    id: z.string(),
    type: z.literal('xrpl'),
    xrplHash: xrplHashSchema,
  })
  .refine(
    (source) => source.id === createXrplSourceId({ hash: source.xrplHash }),
    'Invalid XRPL source ID',
  )

/** 登録元と Stake 同期キーの一致を検証する Cardano アカウント schema。 */
export const accountCardanoSchema = createSelectSchema(accountTable)
  .pick({
    id: true,
    group: true,
    name: true,
    type: true,
    category: true,
    syncing: true,
    lastSyncedAt: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
  })
  .extend({
    type: z.literal('cardano'),
    cardanoAddress: z.string(),
    cardanoStakeAddress: cardanoStakeAddressSchema.nullable(),
    syncing: z
      .boolean()
      .nullable()
      .transform((value) => value ?? false),
    cardanoIndexComplete: z
      .boolean()
      .nullable()
      .transform((value) => value ?? false),
  })
  .superRefine((account, context) => {
    const address = cardanoAddressSchema.safeParse(account.cardanoAddress)
    if (
      !address.success ||
      address.data.address !== account.cardanoAddress ||
      address.data.stakeAddress !== account.cardanoStakeAddress
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cardano account address does not match stake key',
      })
    }
  })

/** Cardano の Tx と仮想報酬 Source を区別し、決定的 ID を検証する。 */
export const actionSourceCardanoSchema = z
  .discriminatedUnion('cardanoKind', [
    z.object({
      id: z.string(),
      type: z.literal('cardano'),
      cardanoKind: z.literal('tx'),
      cardanoTxHash: cardanoTxHashSchema,
    }),
    z.object({
      id: z.string(),
      type: z.literal('cardano'),
      cardanoKind: z.literal('reward'),
      cardanoStakeAddress: cardanoStakeAddressSchema,
      cardanoPayoutEpoch: z.number().int().nonnegative().safe(),
    }),
  ])
  .superRefine((source, context) => {
    const expected =
      source.cardanoKind === 'tx'
        ? createCardanoTxSourceId({ txHash: source.cardanoTxHash })
        : createCardanoRewardSourceId({
            stakeAddress: source.cardanoStakeAddress,
            payoutEpoch: source.cardanoPayoutEpoch,
          })
    if (source.id !== expected)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid Cardano source ID',
      })
  })

export const commentCandidatePartSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text'),
    value: z.string(),
  }),
  z.object({
    type: z.literal('action-year'),
  }),
  z.object({
    type: z.literal('action-month'),
  }),
  z.object({
    type: z.literal('action-day'),
  }),
  z.object({
    type: z.literal('action-date'),
  }),
  z.object({
    type: z.literal('action-datetime'),
  }),
  z.object({
    type: z.literal('sequence'),
    digits: z.number().int().positive(),
  }),
])

export const commentCandidatePartsSchema = z.array(commentCandidatePartSchema)

export const commentCandidateSchema = createSelectSchema(commentCandidateTable)
  .omit({
    displayOrder: true,
  })
  .extend({
    parts: commentCandidatePartsSchema,
  })

export const insertCommentCandidateSchema = createInsertSchema(
  commentCandidateTable,
)
  .omit({
    displayOrder: true,
  })
  .extend({
    parts: commentCandidatePartsSchema,
  })

export const historyFilterPresetValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.array(z.number()),
  z.array(z.boolean()),
])

export const historyFilterPresetFiltersSchema = z.record(
  historyFilterPresetValueSchema,
)

export const historyFilterPresetSchema = createSelectSchema(
  historyFilterPresetTable,
)
  .omit({
    displayOrder: true,
  })
  .extend({
    filters: historyFilterPresetFiltersSchema,
  })

export const insertHistoryFilterPresetSchema = createInsertSchema(
  historyFilterPresetTable,
)
  .omit({
    displayOrder: true,
  })
  .extend({
    filters: historyFilterPresetFiltersSchema,
  })

export const insertAccountEvmSchema = createInsertSchema(accountTable)
  .extend({
    type: z.literal('evm'),
    evmChainId: z.number().int(),
  })
  .omit({
    polkadotChainId: true,
    polkadotAddress: true,
    polkadotToBlock: true,
    solanaAddress: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
    xrplAddress: true,
    xrplIndexComplete: true,
    exchangeApiKey: true,
    exchangeApiSecret: true,
    exchangeApiPassword: true,
  })

export const accountPolkadotSchema = createSelectSchema(accountTable)
  .extend({
    type: z.literal('polkadot'),
    polkadotChainId: z.number().int(),
    polkadotAddress: z.string().regex(/^\w+$/),
  })
  .omit({
    evmChainId: true,
    evmAddress: true,
    evmToBlock: true,
    solanaAddress: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
    xrplAddress: true,
    xrplIndexComplete: true,
    exchangeApiKey: true,
    exchangeApiSecret: true,
    exchangeApiPassword: true,
  })

export const accountSolanaSchema = createSelectSchema(accountTable)
  .extend({
    type: z.literal('solana'),
    solanaAddress: z.string().regex(/^\w+$/),
  })
  .omit({
    evmChainId: true,
    evmAddress: true,
    evmToBlock: true,
    polkadotChainId: true,
    polkadotAddress: true,
    polkadotToBlock: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
    xrplAddress: true,
    xrplIndexComplete: true,
    exchangeApiKey: true,
    exchangeApiSecret: true,
    exchangeApiPassword: true,
  })

export const insertAccountSolanaSchema = createInsertSchema(accountTable)
  .extend({
    type: z.literal('solana'),
    solanaAddress: z.string().regex(/^\w+$/),
  })
  .omit({
    evmChainId: true,
    evmAddress: true,
    evmToBlock: true,
    polkadotChainId: true,
    polkadotAddress: true,
    polkadotToBlock: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
    xrplAddress: true,
    xrplIndexComplete: true,
    exchangeApiKey: true,
    exchangeApiSecret: true,
    exchangeApiPassword: true,
  })

export const accountExchangeSchema = createSelectSchema(accountTable)
  .extend({
    type: z.literal('exchange'),
    exchangeApiKey: z
      .string()
      .nullable()
      .transform((v) => v ?? undefined),
    exchangeApiSecret: z
      .string()
      .nullable()
      .transform((v) => v ?? undefined),
    exchangeApiPassword: z
      .string()
      .nullable()
      .transform((v) => v ?? undefined),
  })
  .omit({
    evmChainId: true,
    evmAddress: true,
    evmToBlock: true,
    polkadotChainId: true,
    polkadotAddress: true,
    polkadotToBlock: true,
    solanaAddress: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
    xrplAddress: true,
    xrplIndexComplete: true,
  })

export const insertAccountExchangeSchema = createInsertSchema(accountTable)
  .extend({
    type: z.literal('exchange'),
  })
  .omit({
    evmChainId: true,
    evmAddress: true,
    evmToBlock: true,
    polkadotChainId: true,
    polkadotAddress: true,
    polkadotToBlock: true,
    solanaAddress: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
    xrplAddress: true,
    xrplIndexComplete: true,
  })

export const accountServiceSchema = createSelectSchema(accountTable)
  .extend({
    type: z.literal('service'),
  })
  .omit({
    evmChainId: true,
    evmAddress: true,
    evmToBlock: true,
    polkadotChainId: true,
    polkadotAddress: true,
    polkadotToBlock: true,
    solanaAddress: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
    xrplAddress: true,
    xrplIndexComplete: true,
    exchangeApiKey: true,
    exchangeApiSecret: true,
    exchangeApiPassword: true,
  })

export const insertAccountServiceSchema = createInsertSchema(accountTable)
  .extend({
    type: z.literal('service'),
  })
  .omit({
    evmChainId: true,
    evmAddress: true,
    evmToBlock: true,
    polkadotChainId: true,
    polkadotAddress: true,
    polkadotToBlock: true,
    solanaAddress: true,
    cardanoAddress: true,
    cardanoStakeAddress: true,
    cardanoIndexComplete: true,
    xrplAddress: true,
    xrplIndexComplete: true,
    exchangeApiKey: true,
    exchangeApiSecret: true,
    exchangeApiPassword: true,
  })

export const accountExchangeApiSchema = createSelectSchema(
  accountExchangeApiTable,
)

export const accountEvmCsvSchema = createSelectSchema(accountEvmCsvTable)

export const accountSolanaSignatureSchema = createSelectSchema(
  accountSolanaSignatureTable,
).extend({
  signature: z.string().regex(/^\w+$/),
})

export const actionSourcePolkadotSchema = createSelectSchema(actionSourceTable)
  .extend({
    type: z.literal('polkadot'),
    polkadotChainId: z.number().int(),
    polkadotBlockNumber: z.number().int(),
    polkadotExtrinsicIndex: z.number().int().optional().nullable(),
  })
  .omit({
    evmChainId: true,
    evmHash: true,
    evmFrom: true,
    evmTo: true,
    evmStatus: true,
    evmFunctionId: true,
    evmFunctionName: true,
    xrplHash: true,
    cardanoKind: true,
    cardanoTxHash: true,
    cardanoStakeAddress: true,
    cardanoPayoutEpoch: true,
    solanaSignature: true,
    solanaStatus: true,
    tokenTransferCount: true,
    tokenTransferCountVersion: true,
  })

export const actionSourceSolanaSchema = createSelectSchema(actionSourceTable)
  .extend({
    type: z.literal('solana'),
    solanaSignature: z.string().regex(/^\w+$/),
    solanaStatus: z.union([z.literal('success'), z.literal('reverted')]),
  })
  .omit({
    evmChainId: true,
    evmHash: true,
    evmFrom: true,
    evmTo: true,
    evmStatus: true,
    evmFunctionId: true,
    evmFunctionName: true,
    xrplHash: true,
    cardanoKind: true,
    cardanoTxHash: true,
    cardanoStakeAddress: true,
    cardanoPayoutEpoch: true,
    polkadotBlockNumber: true,
    polkadotChainId: true,
    polkadotExtrinsicIndex: true,
  })

export const insertActionSourceSolanaSchema = createInsertSchema(
  actionSourceTable,
)
  .extend({
    solanaSignature: z.string().regex(/^\w+$/),
    solanaStatus: z.union([z.literal('success'), z.literal('reverted')]),
  })
  .omit({
    evmChainId: true,
    evmHash: true,
    evmFrom: true,
    evmTo: true,
    evmStatus: true,
    evmFunctionId: true,
    evmFunctionName: true,
    xrplHash: true,
    cardanoKind: true,
    cardanoTxHash: true,
    cardanoStakeAddress: true,
    cardanoPayoutEpoch: true,
  })

/** 提案とActionの関連種別を検証するschema。 */
export const assistantProposalActionRoleSchema = z.enum(['target', 'reference'])

/** 保存済みアシスタント提案を検証するschema。 */
export const assistantProposalSchema = createSelectSchema(
  assistantProposalTable,
)

/** 新規アシスタント提案を検証するschema。 */
export const insertAssistantProposalSchema = createInsertSchema(
  assistantProposalTable,
)

/** 保存済み提案とActionの関連を検証するschema。 */
export const assistantProposalActionSchema = createSelectSchema(
  assistantProposalActionTable,
).extend({
  role: assistantProposalActionRoleSchema,
})

/** 新規提案とActionの関連を検証するschema。 */
export const insertAssistantProposalActionSchema = createInsertSchema(
  assistantProposalActionTable,
).extend({
  role: assistantProposalActionRoleSchema,
})

/** 保存済み拒否フィンガープリントを検証するschema。 */
export const assistantRejectedFingerprintSchema = createSelectSchema(
  assistantRejectedFingerprintTable,
)

/** 新規拒否フィンガープリントを検証するschema。 */
export const insertAssistantRejectedFingerprintSchema = createInsertSchema(
  assistantRejectedFingerprintTable,
)

export const insertSingleActionRuleSchema = createInsertSchema(
  singleActionRuleTable,
)

export const accountingPeriodSchema = createSelectSchema(
  accountingPeriodTable,
).extend({
  start: z.date().transform((v) => v.getTime()),
  end: z.date().transform((v) => v.getTime()),
})

export const insertAccountingPeriodSchema = createInsertSchema(
  accountingPeriodTable,
).extend({
  id: z.number().int().positive(),
})
